import React, { useEffect, useRef, useState } from 'react';
import { CANVAS_WIDTH, getCanvasHeight, type StudioPageData, type StudioSettings } from '../../constants';
import StudioPage from './StudioPage';

interface StudioCanvasProps {
  page: StudioPageData;
  settings: StudioSettings;
  pageNumber?: number;
  className?: string;
  align?: 'center' | 'start';
}

const StudioCanvas: React.FC<StudioCanvasProps> = ({ page, settings, pageNumber, className, align = 'center' }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const canvasHeight = getCanvasHeight(settings.format);
  const alignStart = align === 'start';

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return undefined;
    }

    const resize = () => {
      const availableWidth = Math.max(host.clientWidth - 8, 1);
      const availableHeight = Math.max(host.clientHeight - (alignStart ? 4 : 8), 1);
      setScale(Math.min(availableWidth / CANVAS_WIDTH, availableHeight / canvasHeight, 1.25));
    };

    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    if (observer) {
      observer.observe(host);
    }
    window.addEventListener('resize', resize);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', resize);
    };
  }, [alignStart, canvasHeight]);

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        alignItems: alignStart ? 'flex-start' : 'center',
        justifyContent: 'center',
        paddingTop: alignStart ? 4 : 0,
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          width: CANVAS_WIDTH * scale,
          height: canvasHeight * scale,
          flexShrink: 0,
          overflow: 'hidden',
          borderRadius: 10,
          boxShadow: '0 18px 48px rgba(26, 51, 112, 0.2)',
          userSelect: 'none',
          ...({ WebkitUserDrag: 'none' } as React.CSSProperties)
        }}
      >
        <div
          style={{
            width: CANVAS_WIDTH,
            height: canvasHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none'
          }}
        >
          <StudioPage page={page} settings={settings} pageNumber={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default StudioCanvas;
